// Batch lead import: a spreadsheet in, validated directory rows out.
//
// The wizard (pages/ImportLeads.jsx) hands this file the parsed rows and a
// column mapping; this file turns them into rows in the shape the masonry
// directory already holds (name, city_state, website, trade, contacts, ...),
// says what was wrong with each one BEFORE anything is sent, files each row
// under its state, and streams the result to the leads API one region at a
// time in chunks the lambda accepts. Nothing here touches React.
//
// Copied from reachwired's lib/leadImport.js (2026-10-20) and grown for this
// app: imported rows join an EXISTING directory (Masonry) as extra "imp-<state>"
// regions instead of becoming a new lead type; every row is validated field by
// field with a reason a person can act on; rows already in the directory are
// skipped instead of doubled; and .xlsx is read here, with no library.
//
// Nothing is enriched on import. Rows land raw and are researched on the spot,
// ten at a time, from the directory's Enrich button.
import { parseCSV } from './csv.js'
import { requireIdToken } from './cognito.js'
import { getApiBase } from './data.jsx'
import { STATE_NAMES } from './leadScope.js'

// Rows per request. The lambda caps a call at 500; 400 keeps a wide sheet
// comfortably under the 6MB proxy payload cap.
export const IMPORT_CHUNK = 400
export const MAX_FILE_BYTES = 25 * 1024 * 1024
// How many individual problems the review keeps for display; the counts are
// always complete.
const MAX_LISTED_ISSUES = 500

// The directory columns a spreadsheet column can feed. `aliases` are the
// header spellings the auto-mapper recognises (compared lowercase, with
// underscores and dashes read as spaces) - including the headers of the
// masonry-leads-advanced Blue Book exports and the finalized workbooks, so
// those files map with no clicks. Anything unmapped is skipped, never guessed:
// a wrong column under "Email" is worse than an empty one.
export const IMPORT_FIELDS = [
  { key: 'name', label: 'Company name', required: true, aliases: ['name', 'company', 'company name', 'business', 'business name', 'organization', 'organisation', 'account', 'account name', 'firm', 'contractor'] },
  { key: 'website', label: 'Website', aliases: ['website', 'web', 'url', 'site', 'domain', 'homepage', 'website url', 'web address'] },
  { key: 'email', label: 'Email', aliases: ['email', 'e-mail', 'email address', 'company email', 'primary email'] },
  { key: 'phone', label: 'Phone (main line)', aliases: ['phone', 'telephone', 'phone number', 'main phone', 'office phone', 'business phone', 'company main line', 'main line'] },
  { key: 'city', label: 'City', aliases: ['city', 'town', 'locality'] },
  { key: 'state', label: 'State', aliases: ['state', 'st', 'province', 'state code', 'state/province'] },
  { key: 'city_state', label: 'City, ST (one column)', aliases: ['city state', 'city, st', 'city/state', 'location', 'city, state', 'company city'] },
  { key: 'full_address', label: 'Street address', aliases: ['address', 'full address', 'street', 'street address', 'address 1', 'address1', 'mailing address', 'company address'] },
  { key: 'zip', label: 'ZIP', aliases: ['zip', 'zip code', 'zipcode', 'postal', 'postal code', 'postcode'] },
  { key: 'trade', label: 'Trade / services', aliases: ['trade', 'trades', 'services', 'service', 'category', 'categories', 'industry', 'specialty', 'specialties', 'business type'] },
  { key: 'contacts', label: 'Contacts (packed list)', aliases: ['contacts', 'contacts full details', 'people', 'contact list'] },
  { key: 'contact_name', label: 'Contact name', aliases: ['contact', 'contact name', 'owner', 'owner name', 'full name', 'decision maker', 'primary contact', 'principal'] },
  { key: 'contact_role', label: 'Contact title', aliases: ['title', 'job title', 'role', 'position', 'contact title', 'contact role'] },
  { key: 'contact_phone', label: 'Contact phone', aliases: ['contact phone', 'mobile', 'cell', 'cell phone', 'direct phone', 'direct'] },
  { key: 'established', label: 'Established (year)', aliases: ['established', 'founded', 'year founded', 'year established', 'in business since'] },
  { key: 'company_size', label: 'Company size', aliases: ['company size', 'employees', 'size', 'headcount', 'employee count', '# employees', 'number of employees'] },
  { key: 'annual_volume', label: 'Annual volume', aliases: ['annual volume', 'revenue', 'annual revenue', 'sales', 'volume', 'annual sales', 'estimated revenue'] },
  { key: 'service_area', label: 'Service area', aliases: ['service area', 'areas served', 'coverage', 'territory', 'markets', 'service areas'] },
  { key: 'certifications', label: 'Certifications', aliases: ['certifications', 'certification', 'licenses', 'license'] },
  { key: 'profile_link', label: 'Profile / source link', aliases: ['profile', 'profile link', 'source url', 'linkedin', 'listing', 'listing url'] },
  { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'comments', 'description', 'about', 'remarks'] },
]
const FIELD_BY_KEY = Object.fromEntries(IMPORT_FIELDS.map((f) => [f.key, f]))

const NEVER_GUESSED_RE = /\b(mobiles|call list|owners|decision makers|score|reasoning|why|pros|cons|objections|angles|estimators?)\b/

const normHeader = (h) =>
  String(h ?? '')
    .toLowerCase()
    .replace(/[_\-./()]+/g, ' ')
    .replace(/[^a-z0-9#, ]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()

/**
 * Column -> field, guessed from the headers. Exact alias matches win, then a
 * contained alias of four letters or more ("Company Website URL" -> website).
 * Each field is assigned once: a second "Phone" column stays unmapped for the
 * person to place, rather than silently overwriting the first.
 */
export function guessMapping(headers) {
  const mapping = {}
  const used = new Set()
  const claim = (header, key) => {
    mapping[header] = key
    used.add(key)
  }
  // Pass 1: exact aliases.
  for (const header of headers) {
    const h = normHeader(header)
    const field = IMPORT_FIELDS.find((f) => !used.has(f.key) && f.aliases.some((a) => normHeader(a) === h))
    if (field) claim(header, field.key)
  }
  // Pass 2: an alias contained in the header, longest alias first so
  // "contact phone" beats "phone". Never for an id column ("company_id" would
  // land on "Company name" by containing "company"), nor for the columns a
  // finalized (already researched) workbook carries - "Direct Mobiles (call
  // list)" is many phones, not the contact's, and "Owners" is research text,
  // not a contact name. Research is what Enrich produces; it is not imported.
  const partial = IMPORT_FIELDS.flatMap((f) => f.aliases.map((a) => ({ key: f.key, alias: normHeader(a) })))
    .filter((p) => p.alias.length >= 4)
    .sort((a, b) => b.alias.length - a.alias.length)
  for (const header of headers) {
    if (mapping[header]) continue
    const h = normHeader(header)
    if (/\bid\b/.test(h) || NEVER_GUESSED_RE.test(h)) continue
    const hit = partial.find((p) => !used.has(p.key) && (h.includes(p.alias) || p.alias.includes(h)) && h.length >= 3)
    if (hit) claim(header, hit.key)
  }
  for (const header of headers) if (!(header in mapping)) mapping[header] = ''
  return mapping
}

// ── geography ────────────────────────────────────────────────────────────────
const NAME_TO_CODE = Object.fromEntries(
  Object.entries(STATE_NAMES).map(([code, name]) => [name.toLowerCase(), code]),
)
const STATE_EXTRA = {
  'washington dc': 'DC',
  'washington d.c.': 'DC',
  'd.c.': 'DC',
  dc: 'DC',
}

/** A state as a postal code, from "IL", "il", "Illinois" or "Washington DC"; '' when it is not one. */
export function stateCodeOf(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const upper = raw.toUpperCase()
  if (/^[A-Z]{2}$/.test(upper) && STATE_NAMES[upper]) return upper
  const lower = raw.toLowerCase().replace(/\s+/g, ' ')
  if (NAME_TO_CODE[lower]) return NAME_TO_CODE[lower]
  if (STATE_EXTRA[lower]) return STATE_EXTRA[lower]
  // "Illinois (IL)", "IL - Illinois"
  const paren = upper.match(/\b([A-Z]{2})\b/)
  if (paren && STATE_NAMES[paren[1]] && /[A-Za-z]{4,}/.test(raw)) return paren[1]
  return ''
}

/** ["Chicago", "IL"] from "Chicago, IL" / "Chicago, Illinois" / "Chicago IL 60601"; missing parts are ''. */
export function splitCityState(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ['', '']
  const comma = raw.match(/^(.*?),\s*([A-Za-z .]{2,}?)(?:\s+\d{5}(?:-\d{4})?)?$/)
  if (comma) {
    const code = stateCodeOf(comma[2].trim())
    if (code) return [comma[1].trim(), code]
  }
  const tail = raw.match(/^(.*?)\s+([A-Za-z]{2})(?:\s+\d{5}(?:-\d{4})?)?$/)
  if (tail) {
    const code = stateCodeOf(tail[2])
    if (code) return [tail[1].trim().replace(/,$/, ''), code]
  }
  const whole = stateCodeOf(raw)
  return whole ? ['', whole] : [raw, '']
}

// ── field rules (the lambda applies the same ones again) ─────────────────────
const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim()

// Values that mean "nothing": a Blue Book export says "Not Provided" for 4,314
// of its revenue cells. Stored, they would read as data.
const PLACEHOLDER_RE = /^(not provided|undisclosed|n\/?a|none|null|unknown|-+|\?+|tbd)$/i

export function normalizeWebsite(v) {
  const s = clean(v).replace(/^(https?:\/\/)?(www\.)?/i, '').split(' ')[0]
  if (!s || !/\.[a-z]{2,}/i.test(s) || s.includes('@')) return ''
  return `https://${s.replace(/\/+$/, '')}`
}

export function normalizePhone(v) {
  let digits = String(v ?? '').replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  if (digits.length !== 10) return ''
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function normalizeYear(v, now = new Date()) {
  const m = String(v ?? '').match(/\b(1[89]\d\d|20\d\d)\b/)
  if (!m) return ''
  const year = Number(m[1])
  return year >= 1800 && year <= now.getFullYear() ? String(year) : ''
}

// FNV-1a twice (two offsets) -> 16 hex chars. Stable across imports, so a
// re-import of the same sheet overwrites the same rows instead of doubling
// the directory, and the wizard can dedupe before sending anything.
function fnv1a(str, seed) {
  let h = seed >>> 0
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h.toString(16).padStart(8, '0')
}
export function importId(name, website, cityState) {
  const basis = [name, website, cityState].map((v) => clean(v).toLowerCase()).join('|')
  return `imp_${fnv1a(basis, 2166136261)}${fnv1a(basis, 0x9747b28c)}`
}

// The company as a dedupe key: lowercase, punctuation and legal suffixes off,
// so "Sooner Masonry, LLC" and "sooner masonry" are one firm.
export function companyNameKey(name) {
  return clean(name)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\b(inc|incorporated|llc|l l c|corp|corporation|co|company|ltd|limited|the)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
const domainOf = (site) =>
  String(site || '')
    .toLowerCase()
    .replace(/^(https?:\/\/)?(www\.)?/, '')
    .split(/[/?#]/)[0]
const cityOf = (cs) => clean(String(cs || '').split(',')[0]).toLowerCase()

/**
 * Who is already in the directory, for "skip what we have". Keys are the
 * company name plus ONE corroborating fact - the main phone or the city -
 * because a name alone merges namesakes in different towns. The website's
 * domain is kept too, but only to SAY so: a multi-branch firm shares one site
 * (Western Specialty Contractors has Orange CA, Garland TX and Tulsa OK
 * offices), and a branch in a new city is a new lead, not a duplicate.
 * Imported rows (company_id imp_...) are left out: re-importing a sheet is how
 * it gets updated, and the same id simply replaces the row.
 */
export function indexExisting(rows) {
  const keys = new Map()
  for (const r of rows || []) {
    if (!r || String(r.company_id || '').startsWith('imp_') || r._crmOnly) continue
    const n = companyNameKey(r.name)
    if (!n) continue
    const label = r.name
    const phone = normalizePhone(r.phone)
    if (phone) keys.set(`${n}|p:${phone}`, label)
    const dom = domainOf(r.website)
    if (dom) keys.set(`${n}|w:${dom}`, label)
    const city = cityOf(r.city_state)
    if (city) keys.set(`${n}|c:${city}`, label)
  }
  return keys
}

// { same, branch }: the existing lead this row duplicates (same name and
// phone, or same name and city), or failing that one it shares a website with
// in another city.
function existingMatch(row, existing) {
  const none = { same: '', branch: '' }
  if (!existing || !existing.size) return none
  const n = companyNameKey(row.name)
  if (!n) return none
  const tries = [row.phone && `${n}|p:${row.phone}`, row.city_state && `${n}|c:${cityOf(row.city_state)}`].filter(Boolean)
  for (const k of tries) if (existing.has(k)) return { same: existing.get(k), branch: '' }
  const site = row.website && `${n}|w:${domainOf(row.website)}`
  return site && existing.has(site) ? { same: '', branch: existing.get(site) } : none
}

// One problem with one cell, phrased for the person fixing the sheet.
// severity: 'error' (row not imported) | 'warning' (value dropped) | 'info'.
function issue(list, counts, entry) {
  counts[entry.problem] = (counts[entry.problem] || 0) + 1
  if (list.length < MAX_LISTED_ISSUES) list.push(entry)
}

/**
 * Directory rows from parsed records + a column mapping, with every problem
 * found on the way.
 *
 * Returns { rows, issues, counts, dropped, merged, existing }:
 *   rows      the rows that will be imported (each carries `_state` and
 *             `_line` for bucketing and messages; both stripped before upload)
 *   issues    [{ line, column, value, problem, action, severity }] - line is
 *             the spreadsheet line (the header is line 1)
 *   counts    problem -> how many times it occurred (complete, not capped)
 *   dropped   rows with no company name (not a lead)
 *   merged    the same company twice in the file
 *   existing  rows already in the directory, skipped (when `skipExisting`)
 */
export function buildImportRows(records, mapping, { existing = null, skipExisting = true, now = new Date() } = {}) {
  const rows = []
  const issues = []
  const counts = {}
  const firstLine = new Map()
  const columnOf = {}
  for (const [header, field] of Object.entries(mapping || {})) if (field && !columnOf[field]) columnOf[field] = header
  let dropped = 0
  let merged = 0
  let skipped = 0
  ;(records || []).forEach((rec, index) => {
    const line = index + 2
    const out = {}
    for (const [header, field] of Object.entries(mapping || {})) {
      if (!field || !FIELD_BY_KEY[field]) continue
      const rawValue = rec?.[header]
      // Packed contacts keep their own separators; everything else is one line.
      const v = field === 'contacts' ? String(rawValue ?? '').trim() : clean(rawValue)
      if (!v) continue
      if (PLACEHOLDER_RE.test(v)) {
        issue(issues, counts, { line, column: header, value: v, problem: 'Placeholder, not a value', action: 'Left empty', severity: 'info' })
        continue
      }
      out[field] = out[field] ? `${out[field]}; ${v}` : v
    }
    const name = out.name
    if (!name) {
      dropped += 1
      issue(issues, counts, { line, column: columnOf.name || '', value: '', problem: 'No company name', action: 'Row skipped', severity: 'error' })
      return
    }
    if (name.length > 200) {
      out.name = name.slice(0, 200)
      issue(issues, counts, { line, column: columnOf.name, value: `${name.slice(0, 40)}…`, problem: 'Company name over 200 characters', action: 'Cut to 200', severity: 'warning' })
    }

    // Geography: a State column, a "City, ST" column, or both.
    const [csCity, csState] = splitCityState(out.city_state)
    const statedState = out.state ? stateCodeOf(out.state) : ''
    if (out.state && !statedState) {
      issue(issues, counts, { line, column: columnOf.state, value: out.state, problem: 'Not a US state', action: 'Filed under Other', severity: 'warning' })
    }
    const state = statedState || csState
    const city = clean(out.city) || csCity
    delete out.city
    delete out.state
    const cityState = [city, state].filter(Boolean).join(', ')
    if (cityState) out.city_state = cityState
    else delete out.city_state
    // An unrecognised State value was reported above; this is for rows that
    // name no state anywhere.
    if (!state && !(columnOf.state && clean(rec?.[columnOf.state]))) {
      issue(issues, counts, { line, column: columnOf.city_state || columnOf.state || '', value: city || '', problem: 'No state', action: 'Filed under Other', severity: 'info' })
    }

    if (out.website) {
      const site = normalizeWebsite(out.website)
      if (site) out.website = site
      else {
        issue(issues, counts, { line, column: columnOf.website, value: out.website, problem: 'Not a website', action: 'Left empty', severity: 'warning' })
        delete out.website
      }
    }
    if (out.email) {
      const first = out.email.split(/[;,\s]+/).find((e) => EMAIL_RE.test(e)) || ''
      if (first) out.email = first.toLowerCase()
      else {
        issue(issues, counts, { line, column: columnOf.email, value: out.email, problem: 'Not an email address', action: 'Left empty', severity: 'warning' })
        delete out.email
      }
    }
    for (const field of ['phone', 'contact_phone']) {
      if (!out[field]) continue
      const phone = normalizePhone(out[field])
      if (phone) out[field] = phone
      else {
        issue(issues, counts, { line, column: columnOf[field], value: out[field], problem: 'Not a 10-digit US phone number', action: 'Left empty', severity: 'warning' })
        delete out[field]
      }
    }
    if (out.established) {
      const year = normalizeYear(out.established, now)
      if (year) out.established = year
      else {
        issue(issues, counts, { line, column: columnOf.established, value: out.established, problem: `Not a year (1800–${now.getFullYear()})`, action: 'Left empty', severity: 'warning' })
        delete out.established
      }
    }
    // A named contact with no packed list becomes one, so research and Apollo
    // have a person to look up - the packed shape the directory already reads.
    if (!out.contacts && out.contact_name) {
      out.contacts = [
        `name=${out.contact_name}`,
        `role=${out.contact_role || ''}`,
        `phone=${(out.contact_phone || '').replace(/\D/g, '')}`,
      ].join('; ')
    }
    if (!out.website && !out.city_state) {
      issue(issues, counts, { line, column: '', value: '', problem: 'No website and no location', action: 'Imported - research will have little to go on', severity: 'warning' })
    }

    out.company_id = importId(out.name, out.website, out.city_state)
    if (firstLine.has(out.company_id)) {
      merged += 1
      issue(issues, counts, { line, column: columnOf.name, value: out.name, problem: `Same company as line ${firstLine.get(out.company_id)}`, action: 'Merged', severity: 'info' })
      return
    }
    firstLine.set(out.company_id, line)
    const already = existingMatch(out, existing)
    if (already.same && skipExisting) {
      skipped += 1
      issue(issues, counts, { line, column: columnOf.name, value: out.name, problem: 'Already in the directory', action: `Skipped (matches “${already.same}”)`, severity: 'info' })
      return
    }
    if (already.branch) {
      issue(issues, counts, { line, column: columnOf.name, value: out.name, problem: 'Same company, another office already listed', action: 'Imported as its own location', severity: 'info' })
    }
    out._state = state
    out._line = line
    rows.push(out)
  })
  return { rows, issues, counts, dropped, merged, existing: skipped }
}

const regionLabelOf = (code) => `${code ? STATE_NAMES[code] || code : 'Other'} (imported)`
const regionSlugOf = (code) =>
  `imp-${
    code
      ? String(STATE_NAMES[code] || code)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
      : 'other'
  }`

/** Rows filed by state: [{ code, slug, label, rows }], biggest first, "Other" last. */
export function bucketByState(rows) {
  const by = new Map()
  for (const row of rows) {
    const code = row._state || ''
    if (!by.has(code)) by.set(code, { code, slug: regionSlugOf(code), label: regionLabelOf(code), rows: [] })
    by.get(code).rows.push(row)
  }
  return [...by.values()].sort((a, b) => {
    if (!a.code !== !b.code) return a.code ? -1 : 1
    return b.rows.length - a.rows.length || a.label.localeCompare(b.label)
  })
}

/** The numbers the review step shows before anything is sent. */
export function summarizeRows(rows) {
  const has = (k) => rows.filter((r) => r[k]).length
  const regions = bucketByState(rows)
  return {
    total: rows.length,
    withWebsite: has('website'),
    withEmail: has('email'),
    withPhone: rows.filter((r) => r.phone || r.contact_phone).length,
    withContact: rows.filter((r) => r.contact_name || r.contacts).length,
    states: regions.filter((r) => r.code).length,
    unplaced: regions.find((r) => !r.code)?.rows.length || 0,
    regions: regions.map((r) => ({ code: r.code, slug: r.slug, label: r.label, count: r.rows.length })),
  }
}

// ── parsing ──────────────────────────────────────────────────────────────────
function headersAndRecords(rawHeaders, records) {
  const headers = []
  const seen = new Set()
  rawHeaders.forEach((h, i) => {
    let name = String(h ?? '').trim() || `Column ${i + 1}`
    while (seen.has(name)) name = `${name} (${i + 1})`
    seen.add(name)
    headers.push(name)
    if (name !== h) {
      for (const rec of records) {
        rec[name] = rec[h]
        delete rec[h]
      }
    }
  })
  return { headers, records }
}

/** CSV or TSV text -> { headers, records }. Blank header cells become "Column N". */
export function parseSpreadsheet(text) {
  const src = String(text || '').replace(/^﻿/, '')
  const firstLine = src.split(/\r?\n/, 1)[0] || ''
  const tabs = (firstLine.match(/\t/g) || []).length
  const commas = (firstLine.match(/,/g) || []).length
  let records
  if (tabs > 0 && tabs >= commas) {
    const lines = src.split(/\r?\n/).filter((l) => l.trim() !== '')
    const head = (lines.shift() || '').split('\t')
    records = lines.map((line) => {
      const cells = line.split('\t')
      const obj = {}
      head.forEach((h, i) => {
        obj[h] = cells[i] ?? ''
      })
      return obj
    })
  } else {
    records = parseCSV(src)
  }
  const rawHeaders = records.length ? Object.keys(records[0]) : firstLine.split(tabs > commas ? '\t' : ',')
  return headersAndRecords(rawHeaders, records)
}

// .xlsx is a zip of XML parts. Read the central directory, inflate the parts
// we need with the browser's own DecompressionStream, and pull the first
// sheet's cells out with the shared-strings table. No library, no upload.
const u16 = (b, o) => b[o] | (b[o + 1] << 8)
const u32 = (b, o) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0

function zipEntries(bytes) {
  let eocd = -1
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i -= 1) {
    if (u32(bytes, i) === 0x06054b50) {
      eocd = i
      break
    }
  }
  if (eocd < 0) throw new Error('not a zip')
  const count = u16(bytes, eocd + 10)
  let p = u32(bytes, eocd + 16)
  const entries = new Map()
  const decoder = new TextDecoder()
  for (let n = 0; n < count; n += 1) {
    if (u32(bytes, p) !== 0x02014b50) throw new Error('bad zip directory')
    const method = u16(bytes, p + 10)
    const size = u32(bytes, p + 20)
    const nameLen = u16(bytes, p + 28)
    const extraLen = u16(bytes, p + 30)
    const commentLen = u16(bytes, p + 32)
    const local = u32(bytes, p + 42)
    const name = decoder.decode(bytes.subarray(p + 46, p + 46 + nameLen))
    entries.set(name, { method, size, local })
    p += 46 + nameLen + extraLen + commentLen
  }
  return entries
}

async function zipText(bytes, entry) {
  const start = entry.local + 30 + u16(bytes, entry.local + 26) + u16(bytes, entry.local + 28)
  const data = bytes.subarray(start, start + entry.size)
  if (entry.method === 0) return new TextDecoder().decode(data)
  if (entry.method !== 8) throw new Error('unsupported zip compression')
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
  return new TextDecoder().decode(await new Response(stream).arrayBuffer())
}

const xmlText = (s) =>
  String(s)
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
const runsText = (xml) => [...String(xml).matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((m) => xmlText(m[1])).join('')
const columnIndex = (ref) => {
  const letters = String(ref || '').match(/^[A-Z]+/)?.[0] || ''
  let n = 0
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n - 1
}

// One worksheet's XML -> rows of cell strings (gaps filled with '').
function sheetGrid(sheet, shared) {
  const grid = []
  for (const rowMatch of sheet.matchAll(/<row\b[^>]*>([\s\S]*?)<\/row>/g)) {
    const cells = []
    let next = 0
    for (const c of rowMatch[1].matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = c[1]
      const ref = attrs.match(/\br="([A-Z]+\d+)"/)?.[1]
      const col = ref ? columnIndex(ref) : next
      next = col + 1
      const type = attrs.match(/\bt="([^"]+)"/)?.[1] || ''
      const inner = c[2] || ''
      const v = inner.match(/<v>([\s\S]*?)<\/v>/)?.[1]
      let value = ''
      if (type === 's') value = shared[Number(v)] ?? ''
      else if (type === 'inlineStr') value = runsText(inner)
      else if (type === 'b') value = v === '1' ? 'TRUE' : v === '0' ? 'FALSE' : ''
      else value = v != null ? xmlText(v) : ''
      cells[col] = value
    }
    grid.push(Array.from(cells, (x) => x ?? ''))
  }
  const filled = (r) => r.some((x) => String(x).trim() !== '')
  const firstFilled = grid.findIndex(filled)
  if (firstFilled < 0) return { head: [], body: [] }
  return { head: grid[firstFilled], body: grid.slice(firstFilled + 1).filter(filled) }
}

/**
 * .xlsx bytes -> { headers, records, sheets, sheetsRead }. The first sheet
 * sets the columns; every later sheet with the SAME header row is read too -
 * the finalized masonry workbooks split one state across region tabs
 * (florida_finalized.xlsx: 88 + 162 rows), and importing only the first tab
 * would silently drop most of the file. A sheet with different columns is a
 * different table and is left out (`sheetsRead` says which were used).
 */
export async function parseXlsx(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
  const entries = zipEntries(bytes)
  const read = async (name) => (entries.has(name) ? zipText(bytes, entries.get(name)) : '')
  const workbook = await read('xl/workbook.xml')
  const sheetTags = [...workbook.matchAll(/<sheet\b[^>]*>/g)].map((m) => m[0])
  const sheets = sheetTags.map((t) => xmlText(t.match(/\bname="([^"]*)"/)?.[1] || ''))
  const rels = await read('xl/_rels/workbook.xml.rels')
  const relTags = [...rels.matchAll(/<Relationship\b[^>]*>/g)].map((m) => m[0])
  const targetOf = (tag, index) => {
    const id = tag.match(/\br:id="([^"]+)"/)?.[1]
    const rel = id ? relTags.find((t) => t.includes(`Id="${id}"`)) : null
    const path = rel?.match(/\bTarget="([^"]+)"/)?.[1]
    if (!path) return `xl/worksheets/sheet${index + 1}.xml`
    return path.startsWith('/') ? path.slice(1) : `xl/${path.replace(/^\.\//, '')}`
  }
  const shared = [...(await read('xl/sharedStrings.xml')).matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => runsText(m[1]))
  const tags = sheetTags.length ? sheetTags : ['']
  let head = null
  const body = []
  const sheetsRead = []
  for (let i = 0; i < tags.length; i += 1) {
    const xml = await read(targetOf(tags[i], i))
    if (!xml) continue
    const grid = sheetGrid(xml, shared)
    if (!grid.head.length) continue
    const key = grid.head.map((h) => String(h).trim()).join('\u0001')
    if (head === null) head = grid.head
    else if (key !== head.map((h) => String(h).trim()).join('\u0001')) continue
    body.push(...grid.body)
    sheetsRead.push(sheets[i] || `Sheet ${i + 1}`)
  }
  if (head === null) {
    if (!sheetTags.length) throw new Error('no worksheet')
    return { headers: [], records: [], sheets, sheetsRead }
  }
  const records = body.map((r) => Object.fromEntries(head.map((h, i) => [h, r[i] ?? ''])))
  return { ...headersAndRecords(head, records), sheets, sheetsRead }
}

/** A File (csv/tsv/txt/xlsx) -> { headers, records, sheets? }. */
export async function parseFile(file) {
  if (/\.xlsx$/i.test(file.name || '') || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    return parseXlsx(await file.arrayBuffer())
  }
  if (/\.xls$/i.test(file.name || '')) {
    throw new ImportError('Old .xls workbooks cannot be read here. Save it as .xlsx or CSV and try again.', 'FORMAT')
  }
  return parseSpreadsheet(await file.text())
}

// ── the API ──────────────────────────────────────────────────────────────────
export class ImportError extends Error {
  constructor(message, code = 'FAILED') {
    super(message)
    this.code = code
  }
}

const NOT_DEPLOYED =
  'The deployed leads API doesn’t have the import route yet. Deploy the leads lambda and try again.'

async function postRegion(category, slug, body, signal) {
  const token = await requireIdToken()
  let res
  try {
    res = await fetch(`${getApiBase()}/leads/${encodeURIComponent(category)}/${encodeURIComponent(slug)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if (err?.name === 'AbortError') throw err
    throw new ImportError('Could not reach the leads API. Check the connection and try again.', 'NETWORK')
  }
  let data = null
  try {
    const text = await res.text()
    data = text ? JSON.parse(text) : null
  } catch {
    data = null
  }
  if (!res.ok || !data || data.error) {
    const msg = String(data?.error || data?.message || '')
    if (res.status === 404 || res.status === 405 || (res.status === 403 && /missing authentication token/i.test(msg))) {
      throw new ImportError(NOT_DEPLOYED, 'NOT_DEPLOYED')
    }
    if (res.status === 401) throw new ImportError(msg || 'Sign in again to import leads.', 'UNAUTHORIZED')
    if (res.status === 403) throw new ImportError(msg || 'Your role can’t import leads.', 'FORBIDDEN')
    if (res.status === 503) throw new ImportError(msg || NOT_DEPLOYED, 'NOT_DEPLOYED')
    throw new ImportError(msg || `Import failed (${res.status})`, 'FAILED')
  }
  return data
}

const stripInternal = ({ _state, _line, ...row }) => row

/**
 * Send validated rows to the server: filed by state, each region in chunks.
 * Sequential on purpose - the lambda recounts a region after every chunk and
 * two chunks of one region in flight would race that recount.
 *
 * Resolves to { sent, written, rejected: [{line, reason}], regions }; throws
 * ImportError. Rows the server refused anyway come back with their line.
 */
export async function importLeads({ category, rows, onProgress, signal }) {
  const regions = bucketByState(rows)
  const total = rows.length
  let sent = 0
  let written = 0
  const rejected = []
  const done = []
  for (const region of regions) {
    let last = null
    for (let i = 0; i < region.rows.length; i += IMPORT_CHUNK) {
      const chunk = region.rows.slice(i, i + IMPORT_CHUNK)
      last = await postRegion(
        category,
        region.slug,
        { action: 'import', region: { label: region.label }, rows: chunk.map(stripInternal) },
        signal,
      )
      written += Number(last.written) || 0
      for (const r of last.rejected || []) rejected.push({ line: chunk[r.index]?._line ?? null, reason: r.reason })
      sent += chunk.length
      onProgress?.({ sent, total, region: region.label })
    }
    if (last?.region) done.push(last.region)
  }
  return { sent, written, rejected, regions: done }
}

/** Remove one imported region. The lambda works within a time budget; loop until done. */
export async function deleteImportedRegion(category, slug, { onProgress, signal } = {}) {
  let removed = 0
  for (let attempt = 0; attempt < 40; attempt += 1) {
    const res = await postRegion(category, slug, { action: 'delete' }, signal)
    removed += Number(res.removed) || 0
    onProgress?.({ removed, remaining: Number(res.remaining) || 0 })
    if (res.done) return { removed }
  }
  throw new ImportError('Removing the imported leads is taking too long. Try again in a moment.', 'FAILED')
}
